<template>
  <div class="taxProtection">
    <div v-if="isFormAvailable">
      <div v-if="isFormLoading">
        <v-progress-circular
          indeterminate
          :size="20"
          color="blue lighten-5"
          class="mr-2"
        ></v-progress-circular
        >Loading..
      </div>
      <div v-else-if="isFormExists">
        <v-form
          ref="form"
          v-model="isFormValid"
          lazy-validation
          :style="{ maxWidth: formMaxWidth + 'px' }"
        >
          <TabForm
            :formElements="formElements"
            :readOnly="!isEditMode"
            @max_column="calculateMaxFormWidth"
          ></TabForm>
        </v-form>
        <v-footer
          v-if="showFormButtons"
          padless
          fixed
          outlined
          class="footerBtnContainer"
          elevation="3"
        >
          <div class="btnContainer">
            <div class="btnActions">
              <v-btn
                v-if="!isEditMode"
                color="error"
                outlined
                @click.stop="onDelete()"
                class="actionBtn mr-4"
                >Delete
              </v-btn>
            </div>
            <v-spacer></v-spacer>

            <v-btn
              outlined
              v-if="!isEditMode && selectedMemberId"
              @click="onEdit"
              class="ml-4"
              color="primary"
            >
              <span v-if="isNew">Create</span>
              <span v-else>Edit</span>
            </v-btn>
            <v-btn outlined v-if="isEditMode" @click="onCancel" class="ml-4" color="primary">
              Cancel
            </v-btn>
            <v-btn
              v-if="isEditMode"
              @click="onSave"
              :disabled="!isFormValid"
              class="ml-4"
              color="primary"
              depressed
            >
              Save
            </v-btn>
          </div>
        </v-footer>
      </div>
    </div>
    <v-alert v-else btype="info" outlined dense> Please, select a member first </v-alert>
    <Dialog ref="confirmationDialog"></Dialog>
  </div>
</template>

<script>
import Dialog from '@/components/layout/Dialog.vue';
import TabForm from '@/components/TabForm.vue';
import Utils from '@/utils.js';

export default {
  name: 'TaxProtection',
  components: {
    TabForm,
    Dialog,
  },
  data() {
    return {
      formElements: [],
      taxProtectionData: null,
      isEditMode: false,
      isFormValid: false,
      formMaxWidth: 500,
      isFormLoading: false,
    };
  },
  mounted: function () {
    if (this.selectedMemberId) {
      this.getTaxProtectionForm();
      this.$root.currentPage = 'TaxProtection';
    } else {
      this.$store.commit('setNotificationMessage', {
        type: 'info',
        text: 'Please, select member',
      });
      this.$router.push({ path: '/' });
    }
  },
  computed: {
    currentPlanId() {
      return this.$store.getters.currentPlanId;
    },
    formName() {
      return this.$route.name;
    },
    selectedMemberId() {
      return this.$store.getters.selectedMemberId;
    },
    isFormExists() {
      return this.formElements && this.formElements.length > 0;
    },
    isFormAvailable() {
      return this.selectedMemberId ? true : false;
    },
    isNew() {
      return !this.taxProtectionData;
    },
    showFormButtons() {
      const userPermission = this.$store.getters.getUserPermission(this.$route.path);
      if (userPermission && userPermission == 'Full') return true;

      return false;
    },
  },
  methods: {
    onEdit() {
      this.isEditMode = true;
    },
    onCancel() {
      this.isEditMode = false;
      if (this.selectedMemberId) this.getTaxProtectionData();
      else this.$router.push({ name: 'MemberSearch', path: '/' });
    },
    onSave() {
      const isFormValid = this.$refs.form.validate();
      if (isFormValid) this.saveData();
    },
    async onDelete() {
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete this record?'
        );
        this.deleteTaskProtection();
      } catch (err) {
        console.log(err);
      }
    },
    calculateMaxFormWidth(maxColumnCount) {
      this.formMaxWidth = maxColumnCount * 400;
    },
    async getTaxProtectionData() {
      if (this.selectedMemberId) {
        try {
          let response = await this.$api.get(
            '/TaxProtection/GetTaxProtection?membershipId=' + this.selectedMemberId
          );
          var data = response.data;
          this.taxProtectionData = data;
          if (data) Utils.setDataToFormElements(data, this.formElements);
          else {
            this.formElements.forEach((x) => {
              x.Value = null;
            });
          }
        } catch (err) {
          console.error(err);
        }
      }
    },
    async saveData() {
      const taxProtection = this.createTaxProtectionData();
      if (this.isNew) this.insertTaxProtection(taxProtection);
      else this.updateTaxProtection(taxProtection);
    },
    async updateTaxProtection(taxProtectionData) {
      try {
        await this.$api.post('/TaxProtection/UpdateTaxProtection', taxProtectionData);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Tax protection was updated',
        });
        this.getTaxProtectionData();
        this.isEditMode = false;
      } catch (err) {
        console.error(err);
      }
    },
    async insertTaxProtection(taxProtectionData) {
      try {
        await this.$api.post('/TaxProtection/InsertTaxProtection', taxProtectionData);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Tax protection was added',
        });
        this.getTaxProtectionData();

        this.isEditMode = false;
      } catch (err) {
        console.error(err);
      }
    },

    async deleteTaskProtection() {
      try {
        await this.$api.delete(
          '/TaxProtection/DeleteTaxProtection?Id=' + this.taxProtectionData.MembershipId
        );
        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Task protection was deleted',
        });
        this.clearData();
      } catch (err) {
        console.log(err.message);
      }
    },

    async getTaxProtectionForm() {
      try {
        this.isFormLoading = true;
        let response = await this.$api.get(
          '/datadictionary/GetFormElements?formName=' +
            this.formName +
            '&planId=' +
            this.currentPlanId
        );
        if (response.data.length)
          this.formElements = response.data.filter((f) => f.Visible == true);
        if (this.selectedMemberId) this.getTaxProtectionData();
        this.isFormLoading = false;
      } catch (err) {
        this.isFormLoading = false;
        console.error(err);
      }
    },
    createTaxProtectionData() {
      const taxProtectionData = {};
      if (this.formElements) {
        this.formElements.forEach((x) => {
          if (x.FieldName && x.FieldType != 'Tab') taxProtectionData[x.FieldName] = x.Value;
        });
      }
      taxProtectionData['MembershipId'] = this.selectedMemberId;
      return taxProtectionData;
    },
    clearData() {
      this.formElements.forEach(function (e) {
        e.Value = '';
      });
    },
  },
};
</script>

<style lang="scss">
.taxProtection {
  margin-bottom: 20px;
  padding-left: 30px;
  padding-right: 30px;

  .v-tabs {
    margin-bottom: 10px;

    .v-tabs-slider {
      width: 0px;
    }

    .v-tab {
      border: 1px solid;
      border-radius: 30px;
      color: #4a1cd4 !important;
      height: 35px;
      margin-right: 10px;

      &.v-tab--active {
        color: white !important;
        background-color: #4a1cd4;
      }
    }
  }
}
.footerBtnContainer {
  .btnContainer {
    margin-left: 20px;
    margin-right: 20px;
    margin-bottom: 12px;
    margin-top: 12px;
    width: 100%;
    display: flex;
  }
}
.v-footer {
  padding: 0px;
}
</style>
